import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { formatCurrency } from "./utils";

export const PRIVACY_STORAGE_KEY = "ctaxi-valores-ocultos";

/**
 * Marcacao usada no lugar do valor quando o modo privacidade esta ligado. E texto, e nao um
 * componente, porque a maioria dos valores do sistema e montada dentro de template strings e
 * de props que esperam texto - um componente JSX nao caberia nesses lugares.
 */
export const MASKED_VALUE = "•••••";

export function getStoredPrivacy(): boolean {
  try {
    return localStorage.getItem(PRIVACY_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function persistPrivacy(hidden: boolean) {
  try {
    localStorage.setItem(PRIVACY_STORAGE_KEY, hidden ? "1" : "0");
  } catch {
    // Preferencia de privacidade nunca deve derrubar a aplicacao.
  }
}

type PrivacyContextValue = {
  hidden: boolean;
  toggle: () => void;
};

const PrivacyContext = createContext<PrivacyContextValue | null>(null);

export function PrivacyProvider({ children }: { children: ReactNode }) {
  const [hidden, setHidden] = useState(getStoredPrivacy);

  // Mantem as abas abertas em sincronia: esconder numa e deixar exposto na outra
  // derrotaria o proposito de quem liga isso para ninguem ver a tela.
  useEffect(() => {
    function handleStorage(event: StorageEvent) {
      if (event.key === PRIVACY_STORAGE_KEY) setHidden(event.newValue === "1");
    }

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const toggle = useCallback(() => {
    setHidden((current) => {
      const next = !current;
      persistPrivacy(next);
      return next;
    });
  }, []);

  const value = useMemo(() => ({ hidden, toggle }), [hidden, toggle]);

  return <PrivacyContext.Provider value={value}>{children}</PrivacyContext.Provider>;
}

export function usePrivacy(): PrivacyContextValue {
  const context = useContext(PrivacyContext);
  // Fora do provedor (testes, telas isoladas) o padrao e mostrar: privacidade e escolha
  // explicita, nunca um efeito colateral de contexto faltando.
  return context ?? { hidden: false, toggle: () => undefined };
}

/**
 * Formata dinheiro respeitando o modo privacidade. Substitui formatCurrency nas telas:
 * mesma assinatura, entao vale igual em JSX, em template string e em prop de texto.
 */
export function useMoney() {
  const { hidden } = usePrivacy();
  return useCallback((value: unknown) => (hidden ? MASKED_VALUE : formatCurrency(value)), [hidden]);
}
